import { nanoid } from 'nanoid';
import React, { useContext, useState, useEffect, useMemo } from 'react';

import { SVError } from 'utils/errors';
import * as helpers from 'utils/helpers';
import { FixedBody } from 'utils/styled';

import { ISideNavContextProps, MenuItem } from './interfaces';

//  This is a custom implementation of context used across the different components for the side navigation. It relies on the following tutorials:
//  https://kentcdodds.com/blog/application-state-management-with-react
//  https://leewarrick.com/blog/the-problem-with-context/?utm_campaign=React%2BNewsletter&utm_medium=email&utm_source=React_Newsletter_190

// https://stackoverflow.com/questions/54577865/react-createcontext-issue-in-typescript (avoid problem where context expected a default value passed in)
const SideNavContext = React.createContext({} as ISideNavContextProps);

const defaultItems: MenuItem[] = [
  {
    id: nanoid(),
    text: 'Find services',
    href: '#',
    itemLevel: 0,
    state: 'Collapsed',
    subItems: [
      {
        id: nanoid(),
        text: 'Transport and driving',
        href: `${helpers.getDomainUrl()}/find-services/transport-and-driving`,
        itemLevel: 1,
        state: 'Collapsed',
        subItems: [
          {
            id: nanoid(),
            text: 'Registration',
            href: `${helpers.getDomainUrl()}/find-services/transport-and-driving/registration`,
            itemLevel: 2,
            state: 'Collapsed',
            subItems: [
              {
                id: nanoid(),
                text: 'Check registration',
                href: `${helpers.getDomainUrl()}/find-services/transport-and-driving/registration/check-registration`,
                itemLevel: 3,
                state: 'Collapsed'
              },
              {
                id: nanoid(),
                text: 'Renew registration',
                href: `${helpers.getDomainUrl()}/find-services/transport-and-driving/registration/renew-registration`,
                itemLevel: 3,
                state: 'Collapsed'
              }
            ]
          }
        ]
      },
      {
        id: nanoid(),
        text: 'Personal',
        href: `${helpers.getDomainUrl()}/find-services/personal`,
        itemLevel: 1,
        state: 'Collapsed',
        subItems: [
          {
            id: nanoid(),
            text: 'Apply for a proof of age card',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/find-services/personal/apply-for-a-proof-of-age-card`
          },
          {
            id: nanoid(),
            text: 'Make a will',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/personal/make-a-will`
          },
          {
            id: nanoid(),
            text: 'Ambulance membership',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/personal/ambulance-membership`,
            subItems: [
              {
                id: nanoid(),
                text: 'Become an Ambulance Victoria member',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/personal/ambulance-membership/become-a-member`
              },
              {
                id: nanoid(),
                text: 'Renew Ambulance Victoria membership',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/personal/ambulance-membership/renew-membership`
              },
              {
                id: nanoid(),
                text: 'Update Ambulance Victoria details',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/personal/ambulance-membership/update-membership`
              }
            ]
          }
        ]
      },
      {
        id: nanoid(),
        text: 'Work and volunteering',
        href: `${helpers.getDomainUrl()}/find-services/work-and-volunteering`,
        itemLevel: 1,
        state: 'Collapsed',
        subItems: [
          {
            id: nanoid(),
            text: 'Working with Children Check',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/work-and-volunteering/working-with-children-check`,
            subItems: [
              {
                id: nanoid(),
                text: 'Change from volunteer to employee',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/work-and-volunteering/working-with-children-check/change-from-volunteer-to-employee`
              },
              {
                id: nanoid(),
                text: 'Renew your Working with Children Check',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/work-and-volunteering/working-with-children-check/renew-your-working-with-children-check`
              },
              {
                id: nanoid(),
                text: 'Update your Working with Children Check details',
                itemLevel: 3,
                state: 'Collapsed',
                href: `${helpers.getDomainUrl()}/find-services/work-and-volunteering/working-with-children-check/update-your-working-with-children-check-details`
              }
            ]
          }
        ]
      },
      {
        id: nanoid(),
        text: 'Outdoor and recreation',
        href: `${helpers.getDomainUrl()}/find-services/outdoor-and-recreation`,
        itemLevel: 1,
        state: 'Collapsed',
        subItems: [
          {
            id: nanoid(),
            text: 'Register a party with the Partysafe program',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/outdoor-and-recreation/register-a-party-with-the-partysafe-program`
          },
          {
            id: nanoid(),
            text: 'Buy a fishing licence',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/outdoor-and-recreation/buy-a-victorian-recreational-fishing-licence`
          },
          {
            id: nanoid(),
            text: 'Find a kangaroo harvester',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/outdoor-and-recreation/find-a-kangaroo-harvester`
          }
        ]
      },
      {
        id: nanoid(),
        text: 'Housing and property',
        href: `${helpers.getDomainUrl()}/find-services/housing-and-property/`,
        itemLevel: 1,
        state: 'Collapsed',
        subItems: [
          {
            id: nanoid(),
            text: 'Register absence from residence',
            itemLevel: 2,
            state: 'Collapsed',
            href: `${helpers.getDomainUrl()}/find-services/housing-and-property/register-your-absence-from-residence`
          }
        ]
      },
      {
        id: nanoid(),
        text: 'Crime and the law',
        itemLevel: 1,
        state: 'Collapsed',
        href: `${helpers.getDomainUrl()}/find-services/crime-and-the-law`
      }
    ]
  },
  {
    id: nanoid(),
    text: 'About us',
    href: '#',
    itemLevel: 0,
    state: 'Collapsed',
    subItems: [
      {
        id: nanoid(),
        text: 'Our story',
        itemLevel: 1,
        state: 'Collapsed',
        href: `${helpers.getDomainUrl()}/about-us/our-story`
      }
    ]
  },
  {
    id: nanoid(),
    text: 'Contact us',
    href: '#',
    itemLevel: 0,
    state: 'Collapsed',
    subItems: [
      {
        id: nanoid(),
        text: 'Chat with us',
        itemLevel: 1,
        state: 'Collapsed',
        href: `${helpers.getDomainUrl()}/contact/chat-with-us`
      },
      {
        id: nanoid(),
        text: 'Send us a message',
        itemLevel: 1,
        state: 'Collapsed',
        href: `${helpers.getDomainUrl()}/contact/send-us-a-message`
      }
    ]
  }
];

/**
 * Get the SideNav context provider with the different state data used across the entire sideNav
 */
export const SideNavProvider: React.FC<{ isOpen: boolean; items: MenuItem[] }> = props => {
  const [isOpen, setIsOpen] = useState(() => props.isOpen ?? false);

  // Use useMemo for optimization reasons. This says don't give back a new state unless isOpen, items changes (similar to components/Wizard/context/context.tsx)
  const value = useMemo(
    () => ({
      isOpen,
      setIsOpen,
      items: props.items ?? defaultItems
    }),
    [isOpen, props.items]
  );

  const clearBackForwardCache = event => {
    if (event.persisted) {
      setIsOpen(false);
    }
  };

  // To close side nav when page is about to be cached by safari:
  // https://web.dev/bfcache/#observe-when-a-page-is-restored-from-bfcache
  useEffect(() => {
    window.addEventListener('pagehide', clearBackForwardCache);
    return () => {
      window.removeEventListener('pagehide', clearBackForwardCache);
    };
  }, []);

  return (
    <SideNavContext.Provider value={value}>
      <FixedBody isEnabled={isOpen} />
      {props.children}
    </SideNavContext.Provider>
  );
};

/**
 * instead of calling useContext directly in our components,  we make our own hook that throws an error if we try to access context outside of the provider
 */
export function useSidenavContext() {
  const context = useContext(SideNavContext);
  if (!context) {
    throw new SVError('SideNavContext must be used with SidenavProvider!');
  }
  return context;
}
